import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Clapperboard, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreateReelDialog = ({ companies, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [topic, setTopic] = useState("");
  const [templateMediaId, setTemplateMediaId] = useState("");
  const [duration, setDuration] = useState(8);
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const company = companies.find((c) => c.id === companyId);
  const videoAllowed = !!company?.ai_video_enabled;

  const selectCompany = async (id) => {
    setCompanyId(id);
    setTemplateMediaId("");
    setImages([]);
    if (!id) return;
    try {
      const r = await axios.get(`${API}/media?company_id=${id}`, { withCredentials: true });
      setImages((r.data || []).filter((m) => (m.content_type || "").startsWith("image/")));
    } catch { /* Bilderliste optional */ }
  };

  const run = async () => {
    if (!companyId) return;
    setBusy(true);
    setPhase("Die KI generiert dein Reel-Video — das dauert 2-5 Minuten...");
    try {
      const r = await axios.post(
        `${API}/ai/generate-video`,
        {
          company_id: companyId,
          create_reel: true,
          topic,
          template_media_id: templateMediaId || null,
          duration,
          size: "720x1280",
          model: "veo-3.1",
        },
        { withCredentials: true },
      );
      const jobId = r.data.id;
      let finished = false;
      for (let i = 0; i < 200; i++) {
        await new Promise((res) => setTimeout(res, 5000));
        if (!mountedRef.current) return;
        const jr = await axios.get(`${API}/ai/generate-video/${jobId}`, { withCredentials: true });
        if (!mountedRef.current) return;
        if (jr.data.status === "done") {
          toast.success("KI-Reel erstellt! Video ist in der Mediathek, der Reel-Entwurf in der Liste.");
          finished = true;
          onCreated?.();
          setOpen(false);
          break;
        }
        if (jr.data.status === "failed") {
          toast.error(jr.data.error || "Video-Generierung fehlgeschlagen");
          finished = true;
          break;
        }
      }
      if (!finished) toast.info("Dauert laenger als erwartet — das Reel erscheint automatisch in der Liste, sobald es fertig ist.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video-Generierung fehlgeschlagen");
    } finally {
      setBusy(false);
      setPhase("");
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white"
        data-testid="create-reel-btn"
      >
        <Clapperboard className="w-4 h-4 mr-1.5" /> KI-Reel erstellen
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">KI-Reel erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium mb-1">Firma</p>
              <select
                value={companyId}
                onChange={(e) => selectCompany(e.target.value)}
                disabled={busy}
                className="w-full h-9 px-2 rounded-md border border-[var(--studio-border)] bg-[var(--studio-bg)] text-sm"
                data-testid="reel-company-select"
              >
                <option value="">Firma waehlen...</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.ai_video_enabled ? "" : " (KI-Videos deaktiviert)"}
                  </option>
                ))}
              </select>
              {company && !videoAllowed && (
                <p className="text-[11px] text-amber-600 mt-1" data-testid="reel-disabled-hint">
                  ⚠ KI-Videos sind fuer diese Firma deaktiviert — aktiviere sie in den Firmen-Einstellungen ("KI-Videos").
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium mb-1">Thema / Briefing (optional)</p>
              <Textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                disabled={busy}
                placeholder="z.B. Sommer-Aktion: Cabrio-Wochenende, 20% Rabatt"
                className="text-sm min-h-[60px]"
                data-testid="reel-topic-input"
              />
            </div>
            <div>
              <p className="text-xs font-medium mb-1">Bild als Vorlage (optional)</p>
              <select
                value={templateMediaId}
                onChange={(e) => setTemplateMediaId(e.target.value)}
                disabled={busy || !companyId}
                className="w-full h-9 px-2 rounded-md border border-[var(--studio-border)] bg-[var(--studio-bg)] text-sm"
                data-testid="reel-template-select"
              >
                <option value="">Keine Vorlage — KI entscheidet frei</option>
                {images.map((m) => (
                  <option key={m.id} value={m.id}>{m.original_filename}</option>
                ))}
              </select>
              <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                Die KI-Bildanalyse des Fotos dient als inhaltliche Vorlage fuer das Video.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium mb-1">Laenge</p>
              <div className="flex gap-2">
                {[4, 8, 12].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDuration(d)}
                    disabled={busy}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      duration === d
                        ? "bg-[#2563EB] text-white border-[#2563EB]"
                        : "bg-[var(--studio-bg)] text-[var(--studio-text-muted)] border-[var(--studio-border)]"
                    }`}
                    data-testid={`reel-duration-${d}`}
                  >
                    {d} Sek.
                  </button>
                ))}
              </div>
            </div>
            {phase && (
              <p className="text-[11px] text-[var(--studio-text-muted)]" data-testid="reel-progress-hint">⏳ {phase}</p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Abbrechen</Button>
              <Button
                onClick={run}
                disabled={busy || !companyId || !videoAllowed}
                className="bg-[#2563EB] hover:bg-[#c41d09] text-white"
                data-testid="reel-generate-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Clapperboard className="w-4 h-4 mr-1.5" />}
                Reel generieren
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
